import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Entry point for the password reset flow. Always shows the same success
 * state regardless of whether the email actually exists, so attackers
 * can't enumerate accounts by comparing error messages.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch {
      // We still flip to sent — the server already returns 200 for both cases;
      // this catch is just for network failures and still shouldn't leak.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--ll-bg)" }}>
      <div className="hidden lg:flex flex-col justify-between p-12 border-r relative ll-grain" style={{ borderColor: "var(--ll-border)", background: "#0b5d9e", color: "white" }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 grid place-items-center bg-white text-[var(--ll-ink)]"><span className="font-display font-bold text-sm">F</span></div>
          <div className="font-display font-bold">Financials Ready</div>
        </div>
        <div>
          <div className="font-display font-bold text-4xl xl:text-5xl leading-tight">Lost your way in?</div>
          <p className="mt-4 text-base/relaxed text-white max-w-md">We&apos;ll email you a secure link so you can set a fresh password. The link expires in 30 minutes.</p>
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-white">Made for finance</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm" data-testid="forgot-password-card">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-3">Password reset</div>
          {sent ? (
            <>
              <div className="font-display font-bold text-3xl mb-2 flex items-center gap-2">
                <CheckCircle2 size={24} className="text-[var(--ll-success)]" /> Check your inbox
              </div>
              <p className="text-sm text-[var(--ll-ink-soft)] mb-6">
                If an account exists for <b>{email}</b>, we just sent a reset link. It&apos;s only valid for 30 minutes.
              </p>
              <Link to="/login" data-testid="forgot-password-back-to-login" className="inline-flex items-center gap-2 text-sm underline underline-offset-4">
                Back to sign in
              </Link>
            </>
          ) : (
            <>
              <div className="font-display font-bold text-3xl mb-2">Reset your password</div>
              <p className="text-sm text-[var(--ll-ink-soft)] mb-5">Type the email you use with Financials Ready and we&apos;ll send a one-time link.</p>
              <form onSubmit={submit}>
                <label className="block text-xs uppercase tracking-widest text-[var(--ll-ink-soft)] mb-1">Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  autoFocus
                  data-testid="forgot-password-email"
                  className="w-full px-3 py-2.5 border bg-white mb-4"
                  style={{ borderColor: "var(--ll-border)" }}
                  placeholder="you@firm.com"
                />
                <button
                  type="submit" disabled={loading || !email.trim()}
                  data-testid="forgot-password-submit"
                  className="w-full bg-[var(--ll-ink)] text-white py-3 hover:bg-[#0b5d9e] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? "Sending…" : <>Email me a reset link <ArrowRight size={15} /></>}
                </button>
              </form>
              <div className="mt-6 text-sm text-[var(--ll-ink-soft)]">
                Remembered it? <Link to="/login" className="text-[var(--ll-ink)] underline underline-offset-4" data-testid="forgot-password-signin-link">Sign in</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
